import * as yup from 'yup';

import { SKU } from 'types/sku';

/**
 * The manufacturer details on an item SKU (ClickUp 86bcg0wkv), shared by the
 * Add and Edit SKU modals. Purchase orders read these: the box cost per MOQ
 * prices an order, items in box turns boxes into units, and the first art
 * number is the default art.
 */

/** One price tier as the form edits it. Text, so a blank row stays blank. */
export type MoqRow = { moq: string; price: string };

export type ManufacturerDetailsValues = {
  artNumbers: string[];
  manufacturerSkuId: string;
  manufacturerDescription: string;
  manufacturerItemsInBox: string;
  manufacturerPalletCount: string;
  manufacturerBoxCostPerMoq: MoqRow[];
  manufacturerUnitCostPerMoq: MoqRow[];
};

export type ManufacturerDetailsPayload = {
  artNumbers?: string[];
  manufacturerSkuId?: string;
  manufacturerDescription?: string;
  manufacturerItemsInBox?: number | null;
  manufacturerPalletCount?: number | null;
  manufacturerBoxCostPerMoq?: Record<string, number>;
  manufacturerUnitCostPerMoq?: Record<string, number>;
};

/** Tiers in ascending MOQ order, the order purchase orders pick them in. */
export const moqToRows = (record?: Record<string, number> | null): MoqRow[] =>
  Object.entries(record || {})
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([moq, price]) => ({ moq, price: String(price) }));

const isBlankRow = (row: MoqRow) =>
  row.moq.trim() === '' && row.price.trim() === '';

/** The map the API stores: `{ "30": 94.1 }`. Fully blank rows are dropped. */
export const rowsToMoq = (rows: MoqRow[]): Record<string, number> =>
  rows
    .filter((row) => !isBlankRow(row))
    .reduce<Record<string, number>>((acc, row) => {
      acc[String(Number(row.moq.trim()))] = Number(row.price.trim());
      return acc;
    }, {});

const toText = (value: unknown) =>
  value === undefined || value === null ? '' : String(value);

export const getManufacturerDetailsValues = (
  sku?: Partial<SKU>,
): ManufacturerDetailsValues => ({
  artNumbers: sku?.artNumbers ?? [],
  manufacturerSkuId: sku?.manufacturerSkuId ?? '',
  manufacturerDescription: sku?.manufacturerDescription ?? '',
  manufacturerItemsInBox: toText(sku?.manufacturerItemsInBox),
  manufacturerPalletCount: toText(sku?.manufacturerPalletCount),
  manufacturerBoxCostPerMoq: moqToRows(sku?.manufacturerBoxCostPerMoq),
  manufacturerUnitCostPerMoq: moqToRows(sku?.manufacturerUnitCostPerMoq),
});

/**
 * What to send. A new SKU leaves out what was not filled in. An edit sends
 * every field, so clearing one clears it: blank text as '', a blank number as
 * null and a table with no rows as {}.
 */
export const toManufacturerDetailsPayload = (
  values: ManufacturerDetailsValues,
  mode: 'create' | 'update',
): ManufacturerDetailsPayload => {
  const text = (value: string) => {
    const trimmed = value.trim();
    return mode === 'create' && trimmed === '' ? undefined : trimmed;
  };
  const count = (value: string) => {
    const trimmed = String(value ?? '').trim();
    if (trimmed === '') {
      return mode === 'create' ? undefined : null;
    }
    return Number(trimmed);
  };
  const moq = (rows: MoqRow[]) => {
    const map = rowsToMoq(rows);
    return mode === 'create' && Object.keys(map).length === 0 ? undefined : map;
  };

  return {
    artNumbers:
      mode === 'create' && values.artNumbers.length === 0
        ? undefined
        : values.artNumbers,
    manufacturerSkuId: text(values.manufacturerSkuId),
    manufacturerDescription: text(values.manufacturerDescription),
    manufacturerItemsInBox: count(values.manufacturerItemsInBox),
    manufacturerPalletCount: count(values.manufacturerPalletCount),
    manufacturerBoxCostPerMoq: moq(values.manufacturerBoxCostPerMoq),
    manufacturerUnitCostPerMoq: moq(values.manufacturerUnitCostPerMoq),
  };
};

const wholeCount = yup.string().matches(/^\s*\d*\s*$/, 'Whole numbers only');

const moqRowSchema = yup.object().shape(
  {
    moq: yup.string().when('price', {
      is: (price: string) => Boolean(price?.trim()),
      then: yup
        .string()
        .required('Enter the quantity')
        .matches(/^\s*[1-9]\d*\s*$/, 'Whole number of at least 1'),
      otherwise: yup
        .string()
        .matches(/^\s*([1-9]\d*)?\s*$/, 'Whole number of at least 1'),
    }),
    price: yup.string().when('moq', {
      is: (moq: string) => Boolean(moq?.trim()),
      then: yup
        .string()
        .required('Enter the price')
        .matches(/^\s*\d*(\.\d+)?\s*$/, 'A price of 0 or more'),
      otherwise: yup.string(),
    }),
  },
  [['moq', 'price']],
);

/** Each MOQ may appear once: a second price for it would be ambiguous. */
const moqRowsSchema = yup
  .array()
  .of(moqRowSchema)
  .max(20, 'At most 20 price tiers')
  .test('unique-moq', 'Each quantity can only be listed once', (rows) => {
    const moqs = (rows || [])
      .map((row: any) => String(row?.moq ?? '').trim())
      .filter(Boolean)
      .map(Number);
    return new Set(moqs).size === moqs.length;
  });

export const manufacturerDetailsSchema = {
  artNumbers: yup.array().of(yup.string()),
  manufacturerSkuId: yup.string(),
  manufacturerDescription: yup.string(),
  manufacturerItemsInBox: wholeCount,
  manufacturerPalletCount: wholeCount,
  manufacturerBoxCostPerMoq: moqRowsSchema,
  manufacturerUnitCostPerMoq: moqRowsSchema,
};
